import { differenceInCalendarMonths, isValid, parseISO } from "date-fns";
import {
	AlertBoxActionEnum,
	type PortfolioAlertBoxDTO,
} from "web_myplann/Infrastructure/Api/Api";

/**
 * Shortest remaining maturity we still offer "Porovnat sazby" for.
 *
 * Bank rate tables are banded by `maturity_from`, which typically starts around
 * five years, and the refinancing calculator ANDs that band with the LTV and
 * amount ones (`findRates` in the native shell). Below this threshold no rate
 * matches and the client lands on "Tuto variantu banka nenabízí" — a dead end.
 * Refinancing the last couple of years of a mortgage makes no economic sense
 * anyway, so the CTA is not offered at all.
 */
const MIN_REMAINING_MONTHS_FOR_REFINANCING = 60;

/**
 * Whether the contract still has enough maturity left for refinancing to be
 * worth offering. An unknown or unparseable `endAt` is treated as viable: the
 * backend assigned the action, and hiding it on missing data would suppress a
 * legitimate CTA.
 */
export const isRefinancingWorthwhile = (
	contractEndAt: string | null | undefined,
	today: Date = new Date(),
): boolean => {
	if (!contractEndAt) {
		return true;
	}
	const endAt = parseISO(contractEndAt);
	if (!isValid(endAt)) {
		return true;
	}
	return (
		differenceInCalendarMonths(endAt, today) >=
		MIN_REMAINING_MONTHS_FOR_REFINANCING
	);
};

export type OfferedActions = {
	primaryAction: AlertBoxActionEnum | null;
	secondaryAction: AlertBoxActionEnum | null;
};

/**
 * The alert's actions with the ones we refuse to offer removed. The backend
 * owns action assignment; this only drops `CompareRates` on contracts too close
 * to maturity for it to lead anywhere. A remaining secondary action is promoted
 * to primary so the box still has its main CTA.
 */
export const resolveOfferedActions = (
	alert: PortfolioAlertBoxDTO,
	contractEndAt: string | null | undefined,
	today: Date = new Date(),
): OfferedActions => {
	const offered = [alert.primaryAction, alert.secondaryAction].filter(
		(action): action is AlertBoxActionEnum =>
			action != null &&
			(action !== AlertBoxActionEnum.CompareRates ||
				isRefinancingWorthwhile(contractEndAt, today)),
	);

	return {
		primaryAction: offered[0] ?? null,
		secondaryAction: offered[1] ?? null,
	};
};
